import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@utils';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'primary' | 'ghost';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, icon, size = 'md', variant = 'default', ...props }, ref) => {
    const baseStyles = 'rounded-full transition-all duration-200 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center';

    const variants = {
      default: 'bg-purple-600 hover:bg-purple-700 text-white',
      primary: 'bg-yellow-500 hover:bg-yellow-600 text-white',
      ghost: 'bg-transparent hover:bg-white/10 text-white',
    };

    const sizes = {
      sm: 'w-8 h-8 text-sm',
      md: 'w-10 h-10 text-base',
      lg: 'w-12 h-12 text-lg',
    };

    return (
      <button
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {icon}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
